import type { AppError, Result } from '@aibi/shared';
import { createContext, useContext } from 'react';
import type { AdminServices } from '../services/admin-services';
import type { AuthState } from '../services/auth-api';

export type SessionStatus =
  | { readonly phase: 'checking' }
  | { readonly phase: 'unreachable'; readonly error: AppError }
  | { readonly phase: 'setup' }
  | { readonly phase: 'login'; readonly expired: boolean }
  | { readonly phase: 'ready' };

export interface AdminSessionValue {
  readonly status: SessionStatus;
  /** Clientes envueltos: un 401 devuelve la UI al inicio de sesion. */
  readonly services: AdminServices;
  readonly refresh: () => void;
  readonly signedIn: (state: AuthState) => void;
  readonly logout: () => Promise<void>;
}

export const AdminSessionContext = createContext<AdminSessionValue | null>(null);

const isResult = (value: unknown): value is Result<unknown, AppError> =>
  typeof value === 'object' && value !== null && 'ok' in value;

/** Envuelve cada metodo asincrono para detectar `UNAUTHORIZED` (sesion caducada). */
export function watchUnauthorized<T extends object>(api: T, onUnauthorized: () => void): T {
  return new Proxy(api, {
    get(target, property, receiver) {
      const value: unknown = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') return value;
      return (...args: unknown[]) => {
        const output: unknown = Reflect.apply(value, target, args);
        if (!(output instanceof Promise)) return output;
        return output.then((result: unknown) => {
          if (isResult(result) && !result.ok && result.error.code === 'UNAUTHORIZED') {
            onUnauthorized();
          }
          return result;
        });
      };
    },
  });
}

export const phaseOf = (state: AuthState): SessionStatus =>
  state.setupRequired
    ? { phase: 'setup' }
    : state.authenticated
      ? { phase: 'ready' }
      : { phase: 'login', expired: false };

export function useAdminSession(): AdminSessionValue {
  const value = useContext(AdminSessionContext);
  if (value === null) throw new Error('useAdminSession requiere <AdminSessionProvider>.');
  return value;
}

/** Clientes de administracion de la sesion activa. */
export const useAdminServices = (): AdminServices => useAdminSession().services;
